import { Timelog, useGetTimelogs } from '@/api/queries/timelog/useGetTimelogs';
import { GetTimelogsInput } from '@/gql/graphql';
import { usePaginationState } from '@/ui/pages/projectPage/components/MaterialsPanel/materialsPanelHooks';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useReducer,
  useState,
} from 'react';
import { AssigneeFilter } from './AssigneeFilter';
import { ListedTimelogs } from './ListedTimelogs';
import { TypeFilter } from './TypeFilter';

export type DialogState =
  | { type: 'createTimelog' }
  | { type: 'editTimelog'; timelogId: string }
  | { type: 'deleteTimelog'; timelogId: string }
  | null;

type DialogAction =
  | { type: 'OPEN_DIALOG'; payload: DialogState }
  | { type: 'CLOSE_DIALOG' };

const dialogReducer = (
  state: DialogState,
  action: DialogAction
): DialogState => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return action.payload;
    case 'CLOSE_DIALOG':
      return null;
    default:
      return state;
  }
};

const useInputPaginationState = (
  pageSize: number,
  defaultInput: Partial<GetTimelogsInput>
) => {
  const { pagination, ...paginationActions } = usePaginationState(pageSize);
  const [input, setInput] = useState<GetTimelogsInput>(defaultInput);

  return {
    reset: () => {
      setInput(defaultInput);
      paginationActions.setCurrentPage(1);
    },
    pagination,
    ...paginationActions,
    input: {
      ...input,
    },
    setSort: (sort: {
      sortDirection: GetTimelogsInput['sortDirection'];
      sortBy: GetTimelogsInput['sortBy'];
    }) => {
      setInput((old) => ({ ...old, ...sort }));
    },
    setFilter: (
      newInput: Partial<Pick<GetTimelogsInput, 'assignees' | 'timelogTypeIds'>>
    ) => {
      setInput((old) => ({ ...old, ...newInput }));
      paginationActions.setCurrentPage(1);
    },
  };
};

type ListedTimelogsContextType = ReturnType<typeof useInputPaginationState> & {
  queryResult?: ReturnType<typeof useGetTimelogs>;
  activeDialog:
    | { type: 'createTimelog' }
    | { type: 'editTimelog'; timelog: Timelog }
    | { type: 'deleteTimelog'; timelog: Timelog }
    | null;
  openDialog: (dialog: DialogState) => void;
  closeDialog: () => void;
};

const ListedTimelogContext = createContext<ListedTimelogsContextType>({
  pagination: { limit: 25, offset: 0 },
  input: { linkedEntities: [] },
  setCurrentPage: () => {},
  setSort: () => {},
  setFilter: () => {},
  reset: () => {},
  activeDialog: null,
  openDialog: () => {},
  closeDialog: () => {},
});

export const useListedTimelogsContext = () => {
  return useContext(ListedTimelogContext);
};

const ListedTimelogsContextProvider: FC<{
  linkedEntity: NonNullable<GetTimelogsInput['linkedEntities']>[number];
  pageSize?: number;
  children: (state: ListedTimelogsContextType) => ReactNode;
}> = (props) => {
  const { linkedEntity, children, pageSize } = props;

  const inputPaginationState = useInputPaginationState(pageSize || 25, {
    linkedEntities: [linkedEntity],
    sortBy: 'CREATED_AT',
    sortDirection: 'DESC',
  });

  const queryResult = useGetTimelogs(
    inputPaginationState.input,
    inputPaginationState.pagination
  );

  const [activeDialog, dispatch] = useReducer(dialogReducer, null);

  const openDialog = (dialog: DialogState) => {
    dispatch({ type: 'OPEN_DIALOG', payload: dialog });
  };

  const closeDialog = () => {
    dispatch({ type: 'CLOSE_DIALOG' });
  };

  let activeDialogWithTimelogData: ListedTimelogsContextType['activeDialog'] =
    null;
  switch (activeDialog?.type) {
    case 'editTimelog':
    case 'deleteTimelog': {
      const timelog = queryResult?.data?.timelogs.items.find(
        (t) => t.id === activeDialog.timelogId
      );

      if (timelog) {
        activeDialogWithTimelogData = {
          type: activeDialog.type,
          timelog,
        };
      }
      break;
    }
    default:
      activeDialogWithTimelogData = activeDialog;
  }

  const providerValue = {
    ...inputPaginationState,
    queryResult: queryResult,
    activeDialog: activeDialogWithTimelogData,
    openDialog,
    closeDialog,
  };

  return (
    <ListedTimelogContext.Provider value={providerValue}>
      {children(providerValue)}
    </ListedTimelogContext.Provider>
  );
};

export {
  AssigneeFilter,
  ListedTimelogs,
  ListedTimelogsContextProvider,
  TypeFilter,
};
